import { type PropsWithChildren, useState } from 'react';
import { type HistoryData } from '@fpsm/cxy-components/BarChart';
import clsx from 'clsx';

import { type BookmakersConfig } from '@cx-modules/types';

import {
  BarChart,
  BidirectionalBarChart,
  Card,
  GridTable,
  PerformanceIndicator,
  Tabs,
  TabsContent,
  useToggle } from
'~/components/OddscheckerPlus/components/cxy';
import { type BettingInsights } from '~/configuration/betting-insights/BettingInsightsConfig';
import {
  type BettingInsightsContent,
  type TrendsIndex,
  type ValueBetResult } from
'~/configuration/betting-insights/types';
import {
  type BettingInsightsUI,
  type StyleSheet } from
'~/configuration/betting-insights/ui/BettingInsightsUI';
import type ODDS_FORMAT from '~/enums/odds_format';
import {
  featureFlags,
  useFeatureFlag } from
'~/providers/feature-flag-provider';
import { trackEvent } from '~/utils/dataLayer';

import { DefaultTableBody } from '../DefaultTableBody';
import { EventDetails } from '../EventDetails';

import { TrendsStatsTable, type TrendType } from './components/StatsTable';
import styles from './styles/Trends.module.css';

export type Team = {
  badgeUrl: string;
  name: string;
  nickName: string;
  participantId: string;
  shortName: string;
};

const Mavericks: Team = {
  participantId: 'ef55c3ac-f782-4b39-ab21-9c22e1a5571c',
  name: 'Dallas Mavericks',
  shortName: 'DAL',
  nickName: 'Mavericks',
  badgeUrl:
  'https://cdn.prod.us.occloud.io/blue/img/team-logos/Q4/ef55c3ac-f782-4b39-ab21-9c22e1a5571c.svg'
};

function Trends({
  children,
  config,
  content,
  result




}: {config: BettingInsights<TrendsIndex>;content: BettingInsightsContent<TrendsIndex>[];result: ValueBetResult;} & PropsWithChildren) {
  return (
    <Tabs
      defaultValue={result.sportsData.history?.length > 5 ? 'last10' : 'last5'}
      style={{ width: '100%' }}>

			<GridTable
        className={styles.trends}
        label={result.subeventName}
        mobileColumns={3}>

				<GridTable.Caption>
					<EventDetails<TrendsIndex>
            content={content}
            renderTitle={config.ui.card.title.render}
            result={result} />

				</GridTable.Caption>
				{children}
			</GridTable>
		</Tabs>);

}

function TrendsBody({
  bookmakers,
  content,
  label,
  oddsFormat,
  resultIndex,
  styles,
  table








}: {bookmakers: BookmakersConfig;content: ValueBetResult;label: string;oddsFormat: ODDS_FORMAT;resultIndex: number;styles: StyleSheet;table: BettingInsightsUI<TrendsIndex>['card']['table'];}) {
  return (
    <DefaultTableBody
      bookmakers={bookmakers}
      content={content}
      label={label}
      oddsFormat={oddsFormat}
      resultIndex={resultIndex}
      styles={styles}
      table={table} />);


}

function TrendsFooter({
  content,
  formatDate,
  resourcesUrl




}: {content: ValueBetResult;formatDate: (date: string, format?: string) => string;resourcesUrl: string;}) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMoreStats = () => {
    if (!isOpen) {
      trackEvent('trends_stats_expand', {
        eventLabel: 'Trends Stats Expanded'
      });
    }

    setIsOpen(!isOpen);
  };

  const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({
    isOpen,
    onChange: toggleMoreStats
  });

  const isBarChartEnabled = useFeatureFlag(
    featureFlags.SUBS__PLUS__TRENDS__BARCHART
  );
  const isStatsTableEnabled = useFeatureFlag(
    featureFlags.SUBS__PLUS__STATS_TABLE
  );

  // 2147 is handicap but we can handle this more gracefully after xmas
  const showBidirectionalBarChart =
  content.betTypeId === 1 || content.betTypeId === 2147;

  return (
    <GridTable.Footer className={styles.footer}>
			<GridTable.Row className={styles.footerRow}>
				<GridTable.Cell
          className={styles.footerCell}
          style={{ gridColumn: '1 / -1' }}>

					{!!content.sportsData?.trends &&
          Object.entries(content.sportsData?.trends).map(
            ([trend, trendData]) => {
              const length = trend.replace('last', '');

              return (
                <TabsContent
                  className={styles.tabContent}
                  key={`${trend}-content-${content.betId}`}
                  style={{ width: '100%' }}
                  value={trend}>

										<>
											{trend !== length &&
                    content.sportsData?.history?.slice(
                      0,
                      Number(length)
                    ) &&
                    <>
														<div className={styles.hitRate}>
															<p>{trendData.trend}</p>
															<PerformanceIndicator
                          hasMaxWidth={false}
                          // todo: clean up this type so casting isn't necessary
                          history={
                          content.sportsData.history as HistoryData[]
                          }
                          hitRate={trendData.hitRate}
                          limit={Number(length)}
                          showMatchInformation
                          spacing="s" />

														</div>
														<div
                        className={clsx(
                          styles.statsTrigger,
                          triggerClass
                        )}
                        {...headingProps}
                        aria-expanded={
                        headingProps['aria-expanded'] as
                        boolean |
                        undefined
                        }
                        data-disabled={
                        !(content.sportsData?.history?.length > 0)
                        }>

															<img
                          alt="Stats Icon"
                          className={styles.statsIcon}
                          src={`${resourcesUrl}/assets/img/plus/stats.svg`} />

															<span>See {isOpen ? 'Less' : 'More'} Stats</span>
															<Indicator />
														</div>

														<div
                        {...sectionProps}
                        className={styles.trendContainer}>

															{isOpen ?
                        <>
																	{isBarChartEnabled &&
                          <div
                            className={styles.barChartWrapper}
                            data-testid={
                            showBidirectionalBarChart ?
                            'bi-directional-bar-chart' :
                            'bar-chart'
                            }>

																			{showBidirectionalBarChart ?
                            <BidirectionalBarChart
                              historyData={
                              content.sportsData.
                              history as HistoryData[]
                              }
                              limit={Number(length)}
                              oppositionTeam={Mavericks} /> :


                            <BarChart
                              flipBarSuccess={
                              content.line?.sign === '-' ||
                              content.line?.sign === 'Under'
                              }
                              historyData={
                              content.sportsData.
                              history as HistoryData[]
                              }
                              limit={Number(length)}
                              line={Number(content.line?.absolute)}
                              oppositionTeam={Mavericks} />

                            }
																		</div>
                          }
																	{content.sportsData.statsTable &&
                          isStatsTableEnabled &&
                          <TrendsStatsTable
                            content={content.sportsData.statsTable}
                            formatDate={formatDate}
                            length={Number(length)}
                            trend={trend as TrendType} />

                          }
																	{!isBarChartEnabled &&
                          !isStatsTableEnabled &&
                          content.sportsData.statsTable &&
                          <Card
                            height="80px"
                            padding="l"
                            shouldCenter
                            variant="secondary">

																				Coming Soon
																			</Card>
                          }
																</> :
                        null}
														</div>
													</>
                    }
										</>
									</TabsContent>);

            }
          )}
				</GridTable.Cell>
			</GridTable.Row>
		</GridTable.Footer>);

}

Trends.Body = TrendsBody;
Trends.Footer = TrendsFooter;

export { Trends };